import { CUES, PRELOAD, cueSpec, type Bus, type CueId } from './cues';

// The audio engine (KOLONY-Audio-Design.md §2, §3). Presentation only — invariant #8: this
// subscribes to what the view already says and never informs a decision, and nothing here is
// reachable from shared/.
//
// THREE things this owns that a naive "play a file" layer does not:
//   §2.2 A SCHEDULER. One view carries a whole batch — a roll emits DICE_ROLLED, up to five
//        SUPPLY_SHORT and one HARVEST_PAID *per paid seat*. Starting eight buffers at t=0
//        sums into clipping, so a batch collapses to one cue per family (highest priority)
//        and the whole engine holds a voice cap.
//   §2.6 THE GESTURE. `unlock()` must run SYNCHRONOUSLY inside a real user gesture — an
//        `await` before `resume()` loses it, which is exactly how "fetch, then start audio"
//        gets written. Buffers are fetched after, never before.
//   §2.7 ONE MUTE, three buses. Relative level lives in the per-cue trim, so muting is a
//        master-gain decision and nothing else has to know.
//   §2.6b THE PHONE (D251). Two things a desktop never shows: WebKit parks a context in an
//        `interrupted` state (a call, the lock screen, another app) that `resume()` has to
//        lift again — so `unlock()` resumes ANY non-running state and is bound to every
//        gesture, not once; and iOS runs Web Audio in the 'ambient' audio session, which the
//        ringer switch silences while a `<video>` plays on regardless. `navigator.audioSession`
//        moves the game to 'playback' — only while it is unmuted, so a muted game never holds
//        the session and interrupts a player's own music for nothing.
//
// With no AudioContext (jsdom, and any browser that refuses one) every method is a no-op:
// the game must not care whether audio exists.

const VOICE_CAP = 8;
/** Bed crossfade. Long enough to read as a scene change rather than an edit. */
const FADE_S = 2;

export interface AudioEngineOpts {
  /** Where the encoded cues live. */
  readonly baseUrl?: string;
  /** Injected for tests; production reads the real constructor off `window`. */
  readonly contextFactory?: () => AudioContext | null;
  /** Injected for tests. */
  readonly fetchImpl?: typeof fetch;
  readonly muted?: boolean;
}

export interface LoopHandle {
  stop(fadeSeconds?: number): void;
}

export interface LoopOpts {
  readonly fadeSeconds?: number;
  /**
   * Fired once the bed has actually started (or has failed to load). The service uses it to
   * retire the OUTGOING bed only when the incoming one is really playing — a fade-out started
   * before the new buffer arrives would leave a silent gap on a cold fetch.
   */
  onSettled?(started: boolean): void;
}

export interface AudioEngine {
  /** True once a context exists and buffers may be fetched. */
  readonly live: boolean;
  /** Call inside a gesture handler, synchronously. Idempotent. */
  unlock(): void;
  play(cue: CueId): void;
  /** One view's worth of cues: family-collapsed, then played together. */
  playBatch(cues: readonly CueId[]): void;
  loop(cue: CueId, opts?: LoopOpts): LoopHandle;
  setMuted(muted: boolean): void;
  isMuted(): boolean;
  dispose(): void;
}

function defaultContextFactory(): AudioContext | null {
  const w = globalThis as unknown as { AudioContext?: new () => AudioContext; webkitAudioContext?: new () => AudioContext };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  return Ctor ? new Ctor() : null;
}

/** Opus-in-WebM where the browser takes it, AAC everywhere else (Safari's long tail). */
function preferredExtension(): 'webm' | 'm4a' {
  const A = (globalThis as unknown as { Audio?: new () => HTMLAudioElement }).Audio;
  if (!A) return 'm4a';
  try {
    return new A().canPlayType('audio/webm; codecs="opus"') === '' ? 'm4a' : 'webm';
  } catch {
    return 'm4a';
  }
}

const gainOf = (db: number): number => Math.pow(10, db / 20);

export function createAudioEngine(opts: AudioEngineOpts = {}): AudioEngine {
  const baseUrl = opts.baseUrl ?? '/assets/audio';
  const makeContext = opts.contextFactory ?? defaultContextFactory;
  const doFetch = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));

  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  const buses = new Map<Bus, GainNode>();
  const buffers = new Map<string, AudioBuffer>();
  const pending = new Map<string, Promise<AudioBuffer | null>>();
  const lastPlayed = new Map<CueId, number>();
  const rotation = new Map<CueId, number>();
  let voices = 0;
  let muted = opts.muted ?? false;
  let disposed = false;
  let ext: 'webm' | 'm4a' = 'm4a';
  /**
   * Beds asked for before the first gesture. A game booted from a URL (`?hotseat=`, `?room=`)
   * asks for its bed before any context can exist, and the service's idempotency means it will
   * never ask again — so a `loop()` with no context used to be a bed lost for the whole game.
   * They wait here and begin the moment `unlock()` has a context.
   */
  const deferred: Array<() => void> = [];

  const applyMaster = (): void => {
    if (master && ctx) master.gain.setTargetAtTime(muted ? 0 : 1, ctx.currentTime, 0.02);
  };

  /**
   * iOS only in practice (the AudioSession API is Safari's; every other UA has no
   * `navigator.audioSession` and this is a no-op). 'playback' is what lets a game with a
   * soundtrack be heard with the ringer switch on; 'ambient' hands the session back the
   * moment the player mutes us. Never touched before a context exists: claiming 'playback'
   * at boot would pause a player's podcast for a page that has not made a sound.
   */
  const applySession = (): void => {
    if (!ctx) return;
    const session = (globalThis as unknown as { navigator?: { audioSession?: { type: string } } }).navigator?.audioSession;
    if (!session) return;
    const want = muted ? 'ambient' : 'playback';
    if (session.type === want) return; // bound to every gesture — re-asserting the category each tap would be noise
    try {
      session.type = want;
    } catch {
      // a UA that knows the attribute but refuses the value — the switch simply stays theirs
    }
  };

  // Beds live under audio/music/ because they are DEFERRED media: fetched only after the
  // first gesture, never on the critical path, and budgeted separately (T-PERF-005).
  //
  // They ship in Opus ONLY. A browser with no Opus (Safari before 15) gets the one-shot
  // cues — which carry information — from the AAC set, and simply no soundtrack: the bed
  // fetch 404s and `load` returns null, which is already silence rather than a failure.
  // 3.4 MB of duplicate fallback MUSIC is not worth what it buys.
  const dirFor = (bus: Bus): string => (bus === 'sfx' ? '' : 'music/');
  const extFor = (bus: Bus): 'webm' | 'm4a' => (bus === 'sfx' ? ext : 'webm');

  const fileFor = (cue: CueId): string => {
    const spec = cueSpec(cue);
    const count = spec.variants ?? 1;
    if (count === 1) return spec.id;
    // Round-robin, not random: the same table position hears the same order, which makes an
    // e2e run reproducible for free (§2.6).
    const n = (rotation.get(cue) ?? 0) % count;
    rotation.set(cue, n + 1);
    return `${spec.id}-${n + 1}`;
  };

  const load = async (name: string, bus: Bus = 'sfx'): Promise<AudioBuffer | null> => {
    const cached = buffers.get(name);
    if (cached) return cached;
    const inflight = pending.get(name);
    if (inflight) return inflight;
    const job = (async (): Promise<AudioBuffer | null> => {
      if (!ctx) return null;
      try {
        const res = await doFetch(`${baseUrl}/${dirFor(bus)}${name}.${extFor(bus)}`);
        if (!res.ok) return null;
        const bytes = await res.arrayBuffer();
        const buf = await ctx.decodeAudioData(bytes);
        buffers.set(name, buf);
        return buf;
      } catch {
        return null; // a missing or undecodable cue is silence, never a crash
      } finally {
        pending.delete(name);
      }
    })();
    pending.set(name, job);
    return job;
  };

  /**
   * Start one sound on its own trim node. The trim is PER SOURCE on purpose: envelopes must
   * never be written onto a bus, because a bus is shared. Fading the music bus down for an
   * outgoing bed and up for the incoming one makes the two fight over one gain — the second
   * `cancelScheduledValues` kills the first fade mid-flight and the outgoing bed is CUT, not
   * faded. It also strands the bus near zero, which is what made the victory fanfare (a
   * one-shot on the same bus) inaudible after `music(null)`.
   */
  const start = (
    spec: { bus: Bus; gainDb: number; loop?: boolean },
    buf: AudioBuffer,
  ): { src: AudioBufferSourceNode; trim: GainNode } | null => {
    const bus = buses.get(spec.bus);
    if (!ctx || !bus) return null;
    if (!spec.loop && voices >= VOICE_CAP) return null;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = spec.loop ?? false;
    const trim = ctx.createGain();
    trim.gain.value = gainOf(spec.gainDb);
    src.connect(trim).connect(bus);
    if (!spec.loop) {
      voices++;
      src.addEventListener('ended', () => {
        voices = Math.max(0, voices - 1);
        trim.disconnect();
      });
    }
    src.start();
    return { src, trim };
  };

  const fire = (cue: CueId): void => {
    if (!ctx || disposed) return;
    const spec = cueSpec(cue);
    const now = ctx.currentTime * 1000;
    const gap = spec.minIntervalMs ?? 0;
    const prev = lastPlayed.get(cue);
    if (gap > 0 && prev !== undefined && now - prev < gap) return;
    lastPlayed.set(cue, now);
    const name = fileFor(cue);
    const buf = buffers.get(name);
    if (buf) {
      start(spec, buf);
      return;
    }
    // Not resident yet: fetch and play when it lands. Late is better than never for a bed;
    // for a one-shot the batch has usually moved on, which is why PRELOAD exists.
    void load(name, spec.bus).then((late) => {
      if (late && !disposed) start(spec, late);
    });
  };

  const engine: AudioEngine = {
    get live(): boolean {
      return ctx !== null;
    },

    unlock(): void {
      if (disposed) return;
      if (!ctx) {
        ctx = makeContext();
        if (!ctx) return;
        ext = preferredExtension();
        master = ctx.createGain();
        master.gain.value = muted ? 0 : 1;
        master.connect(ctx.destination);
        for (const bus of ['music', 'sfx', 'ambient'] as const) {
          const node = ctx.createGain();
          node.gain.value = 1;
          node.connect(master);
          buses.set(bus, node);
        }
        for (const cue of PRELOAD) void load(cueSpec(cue).variants ? `${cue}-1` : cue, cueSpec(cue).bus);
        for (const begin of deferred.splice(0)) begin();
      }
      // resume() must be reached with no await in front of it — see §2.6. ANY state but
      // running: 'suspended' before the first gesture, and WebKit's 'interrupted' after a call
      // or a trip to the background, which the DOM types do not spell and a `=== 'suspended'`
      // test silently ignored. A rejection (a context closed under us) is not an event.
      if (ctx.state !== 'running') ctx.resume().catch(() => undefined);
      applySession();
    },

    play(cue: CueId): void {
      fire(cue);
    },

    playBatch(cues: readonly CueId[]): void {
      if (cues.length === 0) return;
      // §2.2: collapse each family to its highest priority, keep everything family-less.
      const best = new Map<string, CueId>();
      const out: CueId[] = [];
      for (const cue of cues) {
        const spec = cueSpec(cue);
        if (!spec.family) {
          out.push(cue);
          continue;
        }
        const held = best.get(spec.family);
        if (held === undefined || (cueSpec(held).priority ?? 0) < (spec.priority ?? 0)) best.set(spec.family, cue);
      }
      for (const cue of best.values()) out.push(cue);
      for (const cue of out) fire(cue);
    },

    loop(cue: CueId, opts: LoopOpts = {}): LoopHandle {
      let voice: { src: AudioBufferSourceNode; trim: GainNode } | null = null;
      let stopped = false;
      const spec = cueSpec(cue);
      const fadeIn = opts.fadeSeconds ?? FADE_S;
      const target = gainOf(spec.gainDb);
      const begin = (): void => {
        if (stopped || disposed) return; // asked for and withdrawn before the first gesture
        void load(spec.id, spec.bus).then((buf) => {
          if (!buf || stopped || disposed || !ctx) {
            opts.onSettled?.(false); // the outgoing bed must retire even when this one cannot start
            return;
          }
          voice = start({ ...spec, loop: true }, buf);
          if (voice) {
            // fade in on THIS source's own trim, from silence to its catalogue level
            const g = voice.trim.gain;
            g.cancelScheduledValues(ctx.currentTime);
            g.setValueAtTime(0.0001, ctx.currentTime);
            g.linearRampToValueAtTime(target, ctx.currentTime + fadeIn);
          }
          opts.onSettled?.(voice !== null);
        });
      };
      // No context yet means no gesture yet: the bed is not refused, it is queued for the
      // unlock that creates one. (`disposed` needs no case — a disposed engine never gets a
      // context, and `begin` checks it anyway.)
      if (ctx) begin();
      else deferred.push(begin);
      return {
        stop(fadeSeconds = FADE_S): void {
          if (stopped) return;
          stopped = true;
          const live = voice;
          voice = null;
          if (!live || !ctx) return;
          const g = live.trim.gain;
          g.cancelScheduledValues(ctx.currentTime);
          g.setValueAtTime(g.value, ctx.currentTime);
          g.linearRampToValueAtTime(0.0001, ctx.currentTime + fadeSeconds);
          const at = ctx.currentTime + fadeSeconds;
          try {
            live.src.stop(at);
          } catch {
            // already stopped — nothing to undo
          }
        },
      };
    },

    setMuted(next: boolean): void {
      muted = next;
      applyMaster();
      applySession();
    },
    isMuted: () => muted,

    dispose(): void {
      disposed = true;
      deferred.length = 0;
      buffers.clear();
      pending.clear();
      buses.clear();
      master?.disconnect();
      master = null;
      const dying = ctx;
      ctx = null;
      void dying?.close().catch(() => undefined);
    },
  };
  void CUES;
  return engine;
}
